import { useContext } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StyledModal, ViewportContext } from "@monorepo/components";
import {
  TUpdateConflictingServiceOnSuccess,
  useUpdateConflictingService,
} from "@monorepo/hooks/src/useUpdateConflictingService";
import theme from "@monorepo/styles/theme";
import { ErrorCodeEnum, IResponseError } from "@monorepo/types";
import CloseIcon from "@mui/icons-material/Close";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button/Button";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

interface IServiceErrorModalProps {
  open: boolean;
  error: IResponseError;
  serviceId: string;
  serviceName: string;
  onModalClose: () => void;
  onSuccess: TUpdateConflictingServiceOnSuccess;
  onError: (err: Array<IResponseError>) => void;
  params: { [x: string]: string };
  wide?: boolean;
}

export const ServiceErrorModal: React.FC<IServiceErrorModalProps> = ({
  open,
  error,
  serviceId,
  serviceName,
  onModalClose,
  onSuccess,
  onError,
  params,
  wide = false,
}) => {
  const { handleSubmit } = useForm();
  const { t } = useTranslation();
  const { isThirdParty } = useContext(ViewportContext);
  const { updateConflictingService } = useUpdateConflictingService({
    serviceId,
    serviceName,
    onSuccess,
    onError,
    params,
  });

  return (
    <>
      <Backdrop
        sx={{
          zIndex: 1000,
          ...(isThirdParty && {
            backgroundColor: "transparent",
          }),
        }}
        open={true}
        onClick={() => {
          onModalClose();
        }}
      >
        <Fade in={open}>
          <StyledModal
            size={wide ? "large" : "medium"}
            sx={{ zIndex: 1001 }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <IconButton
              aria-label={t("aria.close_modal")}
              sx={{ alignSelf: "flex-end" }}
              onClick={(e) => {
                e.stopPropagation();
                onModalClose();
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  padding: theme.spacing(0, 2),
                }}
              >
                <form
                  id="update-service-in-cart"
                  name="update-service-in-cart"
                  onSubmit={handleSubmit(() => {
                    updateConflictingService({ error });
                  })}
                >
                  <Typography sx={{ margin: `${theme.spacing(2)} 0px ${theme.spacing(5)}`, textAlign: "left" }}>
                    {error.title}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: {
                        xs: "column-reverse",
                        sm: "row",
                      },
                      columnGap: theme.spacing(2),
                      rowGap: theme.spacing(1),
                      justifyContent: "space-between",
                    }}
                  >
                    <Button sx={{ flex: 1 }} disableElevation variant="text" onClick={() => onModalClose()}>
                      {t("common.edit")}
                    </Button>
                    <Button
                      sx={{
                        flex: 1,
                        [theme.breakpoints.down("sm")]: {
                          mb: theme.spacing(1),
                        },
                      }}
                      type="submit"
                      disableElevation
                      variant="contained"
                      autoFocus
                    >
                      {Number(error.status) === ErrorCodeEnum.CONFLICT
                        ? t("common.override")
                        : t("cart.understood_save")}
                    </Button>
                  </Box>
                </form>
              </Box>
            </Box>
          </StyledModal>
        </Fade>
      </Backdrop>
    </>
  );
};
